import shpwrite from "@mapbox/shp-write";
import { arcgisToGeoJSON } from "@terraformer/arcgis";
import download from "downloadjs";
import { getFeatureNameFields } from "../components/Dashboard/Editor/helpers";
import {
  BiMapAltIcon,
  BsTableIcon,
  FaFileArchiveIcon,
  GrCircleInformation,
} from "../components/Icons";
import { getLayerTitle } from "../esri/custom-popup-content";
import {
  asOfDateFieldName,
  currAsOfDateFieldName,
  showpublicFieldName,
} from "./API";
import {
  getFieldsByPattern,
  renderCell,
  ROLE_EDITOR,
  triggerGAEvent,
} from "./helper";

const geometryTypes = new Set(["point", "line", "polygon", "polyline"]);

const checkLayerForFeatureTable = (layer, show) => {
  return (
    layer.layerConfig &&
    layer.layerConfig.editable &&
    (show ? layer.visible : !show) &&
    layer.layerConfig.featureTable.isShownInFeatureTable &&
    layer.geometryType &&
    geometryTypes.has(layer.geometryType)
  );
};

const generateFields = (currLayer, config) => {
  const { layerConfig } = currLayer || {};
  if (!layerConfig) return [];

  const situationalFields = getFieldsByPattern(
    currLayer,
    layerConfig.situationalFields,
    config.role !== ROLE_EDITOR
  ).sort((a, b) => {
    if (a.name === currAsOfDateFieldName) return 1;
    if (b.name === currAsOfDateFieldName) return -1;
    return 0;
  });

  const baselineFields = getFieldsByPattern(
    currLayer,
    layerConfig.baselineFields,
    config.role !== ROLE_EDITOR
  ).sort((a, b) => {
    if (a.name === asOfDateFieldName) return 1;
    if (b.name === asOfDateFieldName) return -1;
    return 0;
  });

  const fields = [...situationalFields, ...baselineFields];
  const featureTableExcludedFields =
    layerConfig?.featureTable?.excludeFields || [];

  const outFields = fields
    .filter((f) => !featureTableExcludedFields.includes(f.name))
    .map((field) => field.name);
  if (layerConfig.featureTable.exportCoordinates) {
    outFields.push("latitude", "longitude");
  }
  return outFields;
};

const generateFeatureTableFields = (layer, config) => {
  if (!layer) return [];
  let result = [];
  const names = getFeatureNameFields(layer);

  const fields = generateFields(layer, config);

  result = layer.fields.filter(
    (field) =>
      fields.includes(field.name) &&
      !(field.name === "latitude" || field.name === "longitude")
  );
  result = result.sort((a, b) => {
    if (names.includes(a.name)) return -1;
    if (names.includes(b.name)) return 1;
    if (a.name === showpublicFieldName) return -1;
    if (b.name === showpublicFieldName) return 1;
    return fields.indexOf(a.name) - fields.indexOf(b.name);
  });

  return result;
};

const getAllowedExports = (layer, config) => {
  const { layerConfig } = layer || {};
  if (!layerConfig) return [];

  const isEditor = config.role === ROLE_EDITOR;
  const { featureTable } = layerConfig;
  const allowedExports = [
    {
      type: "csv",
      disabled: false,
      allowed: isEditor
        ? featureTable.allowedExtractionCSVForEditors
        : featureTable.allowedExtractionCSVForPublic,
      Icon: BsTableIcon,
    },
    {
      type: "shapefile",
      disabled: layer.geometryType !== "point",
      allowed: isEditor
        ? featureTable.allowedExtractionCSVForEditors
        : featureTable.allowedExtractionSHPForPublic &&
          featureTable.exportCoordinates,
      Icon: FaFileArchiveIcon,
    },
    {
      type: "geojson",
      disabled: false,
      allowed: isEditor
        ? featureTable.allowedExtractionGEOForEditors
        : featureTable.allowedExtractionGEOForPublic &&
          featureTable.exportCoordinates,
      Icon: BiMapAltIcon,
    },
  ].filter(({ allowed }) => allowed);

  if (allowedExports.length > 0) {
    allowedExports.push({
      type: "metadata",
      disabled: false,
      allowed: true,
      Icon: GrCircleInformation,
    });
  }

  return allowedExports;
};

function convertTimestampToDateString(timestamp) {
  if (typeof timestamp == "number") {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is zero-based
    const day = String(date.getDate()).padStart(2, "0");
    const hour = String(date.getHours()).padStart(2, "0");
    const minute = String(date.getMinutes()).padStart(2, "0");

    // Format the components into the "yyyy-mm-dd hh:mm" string
    return `${year}-${month}-${day} ${hour}:${minute}`;
  }
  return timestamp;
}

const isDateAttribute = (key, value) => {
  const regex = /.*date.*/i;
  return typeof value === "number" && key.match(regex);
};

function getDataFromResults(resultFeatures) {
  return resultFeatures.map((row) => {
    const { attributes, geometry } = row;
    for (const [key, value] of Object.entries(attributes)) {
      if (isDateAttribute(key, value)) {
        attributes[key] = convertTimestampToDateString(value);
      } else if (
        attributes[key] === null ||
        attributes[key] === "Unspecified"
      ) {
        attributes[key] = "";
      }
    }

    if (geometry && geometry.latitude && geometry.longitude) {
      attributes.latitude = geometry.latitude;
      attributes.longitude = geometry.longitude;
    }

    return attributes;
  });
}

const convertToCSV = (objArray, fields, t) => {
  const csvData = typeof objArray != "object" ? JSON.parse(objArray) : objArray;
  let str = "";

  for (let x = 0; x < csvData.length; x++) {
    let line = "";
    const attr = fields.length > 0 ? fields : Object.keys(csvData[x]);
    attr.sort((a, b) =>
      fields.indexOf(a) >= 0 && fields.indexOf(b) >= 0
        ? fields.indexOf(a) - fields.indexOf(b)
        : 0
    );
    for (let y of attr) {
      if (line !== "") {
        line += ",";
      }

      const text =
        csvData[x][y] !== null
          ? t(`layer.domain.${y}.${csvData[x][y]}`, csvData[x][y])
          : "";

      csvData[x][y] = text.search(/layer.domain/) === -1 ? text : csvData[x][y];
      csvData[x][y] =
        typeof csvData[x][y] == "string"
          ? csvData[x][y]
          : csvData[x][y] == null
          ? ""
          : String(csvData[x][y]);
      line += `"${csvData[x][y].replace(/"/g, '""')}"`;
    }

    str += line + "\r\n";
  }

  return str;
};

const exportCSVFile = (headers, csvData, fileName, fields, t) => {
  if (headers) {
    csvData.unshift(headers);
  }

  let jsonCsvData = JSON.stringify(csvData);
  const csv = convertToCSV(jsonCsvData, fields, t);

  const csvBlob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(csvBlob, fileName);
  } else {
    const link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(csvBlob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

const convertToGeoJSON = (
  resultFeatures,
  fields,
  currLayer,
  t,
  isDateAttribute,
  convertTimestampToDateString
) => {
  const validFeatures = resultFeatures.filter(
    (feature) => feature.geometry != null
  );

  // Create a consistent property structure
  const propertyStructure = {};
  fields.forEach((field) => {
    propertyStructure[field] = ""; // Default empty value
  });

  const geoJSON = {
    type: "FeatureCollection",
    features: validFeatures
      .map((item) => {
        // Ensure we have both geometry and attributes
        if (!item.geometry || !item.attributes) {
          console.warn("Feature missing geometry or attributes:", item);
          return null;
        }

        try {
          const geoJSONFeature = arcgisToGeoJSON(item, currLayer.objectIdField);
          // Handle different geometry types
          if (item.geometry) {
            switch (item.geometry.type) {
              case "point":
                geoJSONFeature.geometry = {
                  type: "Point",
                  coordinates: [
                    item.geometry.longitude,
                    item.geometry.latitude,
                  ],
                };
                break;

              case "polyline":
                if (!item.geometry.paths || !item.geometry.paths.length) {
                  console.warn("Invalid polyline geometry:", item.geometry);
                  return null;
                }
                geoJSONFeature.geometry = {
                  type:
                    item.geometry.paths.length === 1
                      ? "LineString"
                      : "MultiLineString",
                  coordinates:
                    item.geometry.paths.length === 1
                      ? item.geometry.paths[0]
                      : item.geometry.paths,
                };
                break;

              case "polygon":
                if (!item.geometry.rings || !item.geometry.rings.length) {
                  console.warn("Invalid polygon geometry:", item.geometry);
                  return null;
                }
                geoJSONFeature.geometry = {
                  type:
                    item.geometry.rings.length === 1
                      ? "Polygon"
                      : "MultiPolygon",
                  coordinates:
                    item.geometry.rings.length === 1
                      ? [item.geometry.rings[0]]
                      : item.geometry.rings.reduce((polygons, ring) => {
                          const area = ring.reduce((a, [x1, y1], i) => {
                            const [x2, y2] = ring[(i + 1) % ring.length];
                            return a + (x2 - x1) * (y2 + y1);
                          }, 0);

                          if (area > 0) {
                            polygons.push([ring]);
                          } else if (polygons.length > 0) {
                            polygons[polygons.length - 1].push(ring);
                          }
                          return polygons;
                        }, []),
                };
                break;

              case "multipoint":
                if (!item.geometry.points || !item.geometry.points.length) {
                  console.warn("Invalid multipoint geometry:", item.geometry);
                  return null;
                }
                geoJSONFeature.geometry = {
                  type: "MultiPoint",
                  coordinates: item.geometry.points,
                };
                break;

              case "extent":
                const { xmin, ymin, xmax, ymax } = item.geometry;
                if (
                  xmin == null ||
                  ymin == null ||
                  xmax == null ||
                  ymax == null
                ) {
                  console.warn("Invalid extent geometry:", item.geometry);
                  return null;
                }
                geoJSONFeature.geometry = {
                  type: "Polygon",
                  coordinates: [
                    [
                      [xmin, ymin],
                      [xmax, ymin],
                      [xmax, ymax],
                      [xmin, ymax],
                      [xmin, ymin],
                    ],
                  ],
                };
                break;

              default:
                console.warn(
                  `Unsupported geometry type: ${item.geometry.type}`
                );
                return null;
            }
          }

          // Start with the consistent property structure
          const newProperties = { ...propertyStructure };

          // Process attributes
          Object.keys(geoJSONFeature.properties || {}).forEach((k) => {
            if (fields.includes(k)) {
              let field = t(`layer.fieldAlias.${k}.title`, k);
              if (field.search(/layer.fieldAlias/) === 0) {
                field = k;
              }

              const attributeValue = geoJSONFeature.properties[k];
              let propertiesValue = !isDateAttribute(k, attributeValue)
                ? t(`layer.domain.${k}.${attributeValue}`, attributeValue)
                : convertTimestampToDateString(attributeValue);

              if (propertiesValue.search(/layer.domain/) === 0) {
                propertiesValue = geoJSONFeature.properties[k];
              }

              newProperties[field] =
                propertiesValue != null ? String(propertiesValue) : "";
            }
          });

          geoJSONFeature.properties = newProperties;
          return geoJSONFeature;
        } catch (error) {
          console.warn("Error processing feature:", error);
          return null;
        }
      })
      .filter((feature) => feature !== null), // Remove any invalid features
  };

  if (geoJSON.features.length === 0) {
    throw new Error("No valid features to export");
  }

  return geoJSON;
};

const exportShapeFile = (currLayer, resultFeatures, fileName, fields, t) => {
  // Filter out features with null geometries
  const geoJSON = convertToGeoJSON(
    resultFeatures,
    fields,
    currLayer,
    t,
    isDateAttribute,
    convertTimestampToDateString
  );

  const options = {
    outputType: "blob",
    compression: "STORE",
    types: {
      point: "POINT",
      polygon: "POLYGON",
      line: "POLYLINE",
    },
  };

  shpwrite
    .zip(geoJSON, options)
    .then((content) => {
      download(content, `${fileName}.zip`, "application/zip");
    })
    .catch((error) => {
      console.error("Error generating shapefile:", error);
      throw new Error("Failed to generate shapefile");
    });
};

const exportGeoJSON = (currLayer, resultFeatures, fileName, fields, t) => {
  const geoJSON = convertToGeoJSON(
    resultFeatures,
    fields,
    currLayer,
    t,
    isDateAttribute,
    convertTimestampToDateString
  );

  // Convert to string and create blob
  const geoJSONString = JSON.stringify(geoJSON, null, 2);
  const blob = new Blob([geoJSONString], {
    type: "application/json",
  });

  // Download file
  download(blob, `${fileName}.geojson`, "application/json");
};

const downloadData = async ({
  layer,
  downloadType,
  t,
  query,
  config,
  columnsOrder,
}) => {
  let message, status;
  try {
    const fields = generateFeatureTableFields(layer, config).map((f) => f.name);

    triggerGAEvent({
      event: "client_action",
      params: {
        action: "download-data",
      },
    });

    if (layer?.layerConfig.featureTable.exportCoordinates) {
      fields.push("latitude", "longitude");
    }

    const layerName = getLayerTitle(layer, t);
    //fetching current features to get latest data
    const res = await layer.queryFeatures(query);
    if (!Array.isArray(res.features)) {
      status = "empty";
      message = t("screen.message.noData", { title: layerName });
      return {
        message,
        status,
      };
    }

    const features = res.features;

    if (features.length) {
      const fileTitle = `${config.alias ? `${config.alias}_` : ""}${layerName}`;
      let date = new Date().toISOString().split("T")[0]; //.replace(/-/g, "");
      const exportedFilename =
        fileTitle !== ""
          ? `${fileTitle}_${String(date)}`
          : `logie_data_${String(date)}`;
      if (downloadType === "shapefile") {
        //export shapefile
        exportShapeFile(layer, features, exportedFilename, fields, t);
      } else if (downloadType === "geojson") {
        exportGeoJSON(layer, features, exportedFilename, fields, t);
      } else {
        const data = getDataFromResults(features);
        const headers = {};
        const entry = data[0];

        Object.keys(data).forEach((attr) => {
          Object.keys(data[attr]).forEach((k) => {
            const field = layer.fields.find((f) => f.name === k);

            if (field && field.domain) {
              data[attr][k] = renderCell(field, data[attr][k], t, config);
            }
          });
        });

        for (let key in entry) {
          if (entry.hasOwnProperty(key)) {
            headers[key] = t("layer.fieldAlias." + key + ".title", key);
          }
        }

        fields.sort((a, b) =>
          columnsOrder.indexOf(a) >= 0 && columnsOrder.indexOf(b) >= 0
            ? columnsOrder.indexOf(a) - columnsOrder.indexOf(b)
            : 0
        );

        exportCSVFile(headers, data, exportedFilename, fields, t);
      }

      message = t("screen.message.downloadSuccess", {
        1: layerName,
      });
      status = "success";
    } else {
      status = "empty";
      message = t("screen.message.noData", { title: layerName });
    }
  } catch (error) {
    console.log(error);
    message = t("screen.message.error");
    status = "fail";
  }

  return {
    message,
    status,
  };
};

function getLayerMetadataAsArray(layer, t) {
  if (!layer || !Array.isArray(layer.fields)) return [];
  const hasDomains = layer.fields.some((f) => f.domain);
  return layer.fields.map((field) => {
    let domainType = "";
    let domainValues = "";

    if (field.domain) {
      // domainName = field.domain.name || "";
      domainType = field.domain.type || "";
      if (
        domainType === "coded-value" &&
        Array.isArray(field.domain.codedValues)
      ) {
        domainValues = field.domain.codedValues
          .map(
            (cv) =>
              `${cv.code}:${t(
                "layer.domain." + field.name + "." + cv.code,
                cv.name
              )}`
          )
          .join("; ");
      } else if (domainType === "range") {
        domainValues = `min:${field.domain.minValue}; max:${field.domain.maxValue}`;
      }
    }

    const headers = {
      [`${t("screen.download.metadata.fieldColumnNames.fieldName")}`]:
        field.name,
      [`${t("screen.download.metadata.fieldColumnNames.fieldTitle")}`]: t(
        "layer.fieldAlias." + field.name + ".title",
        field.alias
      ),
      [`${t("screen.download.metadata.fieldColumnNames.fieldDescription")}`]: t(
        "layer.fieldAlias." + field.name + ".information",
        ""
      ),
      [`${t("screen.download.metadata.fieldColumnNames.type")}`]: field.type,
      [`${t("screen.download.metadata.fieldColumnNames.nullable")}`]:
        field.nullable,
      [`${t("screen.download.metadata.fieldColumnNames.length")}`]:
        field.length || "",
    };

    if (hasDomains) {
      headers[`${t("screen.download.metadata.fieldColumnNames.domainType")}`] =
        domainType;
      headers[
        `${t("screen.download.metadata.fieldColumnNames.domainValues")}`
      ] = domainValues;
    }

    return headers;
  });
}

function downloadLayerMetadataCSV(layer, t) {
  try {
    const layerName = getLayerTitle(layer, t);
    const fileTitle = `${layerName}`;
    const date = new Date().toISOString().split("T")[0]; //.replace(/-/g, "");
    const exportedFilename =
      fileTitle !== ""
        ? `${fileTitle}_metadata_${String(date)}`
        : `${layer.title}_metadata__${String(date)}`;
    const metadataArray = getLayerMetadataAsArray(layer, t);

    if (!metadataArray.length) return;

    // Use the keys of the first object as headers
    const headers = {};
    Object.keys(metadataArray[0]).forEach((key) => {
      headers[key] = key;
    });

    // Use your existing exportCSVFile function
    exportCSVFile(
      headers,
      metadataArray,
      `${exportedFilename}.csv`,
      Object.keys(headers),
      t
    );
  } catch (err) {
    console.log(err);
  }
}

export {
  checkLayerForFeatureTable,
  downloadData,
  downloadLayerMetadataCSV,
  exportShapeFile,
  generateFeatureTableFields,
  geometryTypes,
  getAllowedExports,
};
